import React from 'react';
import { Box, Typography, Paper, IconButton, TextField } from '@mui/material';
import { ArrowBack, MoreVert } from '@mui/icons-material';
import PropTypes from 'prop-types';
import './CanvasHeader.css';
import { pageHeaderSx, pageTitleSx } from '../../../constants/pageLayout';

const CanvasPageHeader = ({
  title,
  onBack,
  onMoreClick,
  titleBarContent,
  isCanvasLocked = false,
  showMoreButton = true,
  isEditable = false,
  onTitleChange,
  onTitleBlur,
  useBpmnJoinBehavior = false,
  afterBackContent,
  ...props
}) => {
  return (
    <Paper
      elevation={0}
      className="fade-in canvas-page-header"
      sx={{
        ...pageHeaderSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        borderRadius: 0,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        width: '100%',
        boxSizing: 'border-box',
        pointerEvents: 'auto',
        overflow: 'hidden',
      }}
      {...props}
    >
      <Box
        className="staggered-animation"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minWidth: 0, // Allow flex items to shrink below their minimum content size
          overflow: 'hidden', // Prevent overflow
        }}
      >
        {/* Back Button and Title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            mr: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={onBack}
            disabled={isCanvasLocked}
            sx={{
              mr: 0.5,
              p: 0.5,
              '&:disabled': {
                color: 'action.disabled',
              },
            }}
          >
            <ArrowBack />
          </IconButton>

          {afterBackContent}

          {isEditable ? (
            <TextField
              variant="standard"
              value={title}
              onChange={onTitleChange}
              onBlur={onTitleBlur}
              placeholder="Untitled"
              disabled={isCanvasLocked}
              InputProps={{
                disableUnderline: true,
                sx: {
                  ...pageTitleSx,
                  '& input': {
                    padding: 0,
                  },
                },
              }}
              sx={{
                minWidth: '100px',
                maxWidth: '200px',
                mr: 0.5,
              }}
            />
          ) : (
            <Typography
              variant="h6"
              component="h1"
              sx={{
                ...pageTitleSx,
                flexShrink: 0,
                mr: 0.5,
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
          )}

          {showMoreButton && (
            <IconButton
              onClick={onMoreClick}
              size="small"
              disabled={isCanvasLocked}
              sx={{
                p: 0.5,
                '&:disabled': {
                  color: 'action.disabled',
                },
              }}
            >
              <MoreVert />
            </IconButton>
          )}

          {/* V1/V2 Mode Badge */}
          <Box
            sx={(theme) => ({
              ml: 1.5,
              display: 'inline-flex',
              alignItems: 'center',
              height: '22px',
              px: '8px',
              borderRadius: '6px',
              fontSize: '0.7rem',
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1,
              border: '1px solid',
              ...(useBpmnJoinBehavior
                ? {
                    color: theme.palette.success.dark,
                    backgroundColor: `${theme.palette.success.main}14`,
                    borderColor: `${theme.palette.success.main}33`,
                  }
                : {
                    color: theme.palette.text.secondary,
                    backgroundColor: `${theme.palette.text.secondary}0F`,
                    borderColor: `${theme.palette.text.secondary}26`,
                  }),
            })}
          >
            {useBpmnJoinBehavior ? 'V2' : 'V1'}
          </Box>
        </Box>

        {/* Title Bar Content (Canvas Buttons) */}
        {titleBarContent && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0, // Allow shrinking
              overflow: 'hidden', // Prevent overflow
              '& > *': {
                flexShrink: 0, // Prevent individual children from shrinking
              },
            }}
          >
            {titleBarContent}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

CanvasPageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onMoreClick: PropTypes.func,
  titleBarContent: PropTypes.node,
  isCanvasLocked: PropTypes.bool,
  showMoreButton: PropTypes.bool,
  isEditable: PropTypes.bool,
  onTitleChange: PropTypes.func,
  onTitleBlur: PropTypes.func,
  useBpmnJoinBehavior: PropTypes.bool,
  afterBackContent: PropTypes.node,
};

export default CanvasPageHeader;
